/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Actionable, View } from '@az/starc-ui';
import NextImage, { blurDataURLPlaceholder } from '@/components/NextImage';
import { type ExtendedProductReviewImage } from '../../../../hooks/useProductReviewsImages';
import { type ActiveImage } from '../../../../hooks/useReviewImageModal';
import styles from './styles.module.scss';
import { useLabels } from '@/hooks/useLabels';

export type GalleryViewProps = {
  images: ExtendedProductReviewImage[];
  onImageClick: (image: ActiveImage, renderBackButton?: boolean) => void;
};

const labelMap = {
  customerImageAltFallback: 'label_reviews_customerImageAltFallback',
};

export const GalleryView = ({ images, onImageClick }: GalleryViewProps) => {
  const labels = useLabels(labelMap);

  return (
    <View
      direction={'row'}
      justify={'start'}
      align={'stretch'}
      gap={{ s: 5, m: 7 }}
      wrap
      className={styles.container}
      attributes={{ 'data-testid': 'gallery-view' }}
    >
      {images.map((image) => (
        <View.Item columns={{ s: 3, m: 3, l: 2 }} key={image.id} className={styles.imageContainer}>
          <Actionable
            className={styles.actionable}
            onClick={() => {
              const withBackButton = true;
              onImageClick({ imageId: image.id, reviewId: image.reviewID }, withBackButton);
            }}
          >
            <NextImage
              src={image.normalPhotoUrl}
              fill
              blurDataURL={blurDataURLPlaceholder}
              alt={image.photoCaption ?? labels.customerImageAltFallback}
            />
          </Actionable>
        </View.Item>
      ))}
    </View>
  );
};
